import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, Trash2, Edit2, Save, Loader2, Calculator, 
  Percent, DollarSign, AlertCircle
} from 'lucide-react';
import { Card, Badge, Modal } from '../../components/ui';
import { cn } from '../../lib/utils';
import { api } from '../../lib/api';

// Types
interface FeeRule {
  id?: string;
  operationType: string;
  calculationType: 'PERCENTAGE' | 'FIXED' | 'PERCENTAGE_PLUS_FIXED';
  percentageValue?: number;
  fixedValue?: number;
  minFee?: number;
  maxFee?: number;
  description?: string;
}

// Operation types
const operationTypes = [
  { value: 'PIX_RECEIVE', label: 'Recebimento Pix', category: 'PIX' },
  { value: 'PIX_SEND', label: 'Envio Pix', category: 'PIX' },
  { value: 'BOLETO_GENERATE', label: 'Emissão Boleto', category: 'BOLETO' },
  { value: 'BOLETO_PAY', label: 'Pagamento Boleto', category: 'BOLETO' },
  { value: 'TRANSFER_INTERNAL', label: 'Transferência Interna', category: 'TRANSFER' },
  { value: 'TRANSFER_TED', label: 'TED', category: 'TRANSFER' },
  { value: 'CARD_PAYMENT', label: 'Pagamento Cartão', category: 'CARD' },
  { value: 'CRYPTO_BUY', label: 'Compra Crypto', category: 'CRYPTO' },
  { value: 'CRYPTO_SELL', label: 'Venda Crypto', category: 'CRYPTO' },
  { value: 'CRYPTO_SWAP', label: 'Swap Crypto', category: 'CRYPTO' },
  { value: 'WITHDRAWAL', label: 'Saque', category: 'OTHER' },
  { value: 'MAINTENANCE', label: 'Manutenção', category: 'OTHER' },
];

const calculationTypes = [
  { value: 'PERCENTAGE', label: 'Percentual', icon: Percent },
  { value: 'FIXED', label: 'Valor Fixo', icon: DollarSign },
  { value: 'PERCENTAGE_PLUS_FIXED', label: 'Percentual + Fixo', icon: Calculator },
];

// Calculate fee based on rule
const calculateFee = (amount: number, rule: FeeRule): number => {
  let fee = 0;

  if (rule.calculationType === 'PERCENTAGE' && rule.percentageValue) {
    fee = amount * (rule.percentageValue / 100);
  } else if (rule.calculationType === 'FIXED' && rule.fixedValue) {
    fee = rule.fixedValue;
  } else if (rule.calculationType === 'PERCENTAGE_PLUS_FIXED') {
    fee = (rule.fixedValue || 0) + amount * ((rule.percentageValue || 0) / 100);
  }

  // Apply min/max
  if (rule.minFee !== undefined && fee < rule.minFee) {
    fee = rule.minFee;
  }
  if (rule.maxFee !== undefined && fee > rule.maxFee) {
    fee = rule.maxFee;
  }

  return fee;
};

// Format currency
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value / 100);
};

interface FeeConfigProps {
  accountId?: string;
}

export const FeeConfig: React.FC<FeeConfigProps> = ({ accountId }) => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<FeeRule | null>(null);
  const [formData, setFormData] = useState<FeeRule>({
    operationType: '',
    calculationType: 'PERCENTAGE',
    percentageValue: 0,
    fixedValue: 0,
  });
  const [previewAmount, setPreviewAmount] = useState(10000); // R$ 100,00 in cents

  // Load fee rules
  const { data: rules = [], isLoading } = useQuery({
    queryKey: ['fee-rules', accountId],
    queryFn: async () => {
      const response = await api.get(`/fee-rules${accountId ? `?accountId=${accountId}` : ''}`);
      return response.data.rules || response.data || [];
    },
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (rule: FeeRule) => {
      if (rule.id) {
        return api.put(`/fee-rules/${rule.id}`, rule);
      }
      return api.post('/fee-rules', { ...rule, accountId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fee-rules'] });
      closeModal();
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (ruleId: string) => {
      return api.delete(`/fee-rules/${ruleId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fee-rules'] });
    },
  });

  // Open modal for new rule
  const openNewRuleModal = () => {
    setEditingRule(null);
    setFormData({
      operationType: '',
      calculationType: 'PERCENTAGE',
      percentageValue: 0,
      fixedValue: 0,
    });
    setIsModalOpen(true);
  };

  // Open modal for editing
  const openEditModal = (rule: FeeRule) => {
    setEditingRule(rule);
    setFormData(rule);
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingRule(null);
    setFormData({
      operationType: '',
      calculationType: 'PERCENTAGE',
      percentageValue: 0,
      fixedValue: 0,
    });
  };

  // Handle save
  const handleSave = () => {
    saveMutation.mutate(formData);
  };

  // Handle delete
  const handleDelete = (ruleId: string) => {
    if (confirm('Tem certeza que deseja excluir esta regra de taxa?')) {
      deleteMutation.mutate(ruleId);
    }
  };

  // Get operation label
  const getOperationLabel = (type: string): string => {
    return operationTypes.find((op) => op.value === type)?.label || type;
  };

  // Get calculation type label
  const getCalculationLabel = (type: string): string => {
    return calculationTypes.find((ct) => ct.value === type)?.label || type;
  };

  // Format rule description
  const formatRuleDescription = (rule: FeeRule): string => {
    const parts: string[] = [];

    if (rule.calculationType === 'PERCENTAGE' && rule.percentageValue) {
      parts.push(`${rule.percentageValue}%`);
    } else if (rule.calculationType === 'FIXED' && rule.fixedValue) {
      parts.push(formatCurrency(rule.fixedValue));
    } else if (rule.calculationType === 'PERCENTAGE_PLUS_FIXED') {
      if (rule.percentageValue) parts.push(`${rule.percentageValue}%`);
      if (rule.fixedValue) parts.push(`+ ${formatCurrency(rule.fixedValue)}`);
    }

    if (rule.minFee) parts.push(`(mín: ${formatCurrency(rule.minFee)})`);
    if (rule.maxFee) parts.push(`(máx: ${formatCurrency(rule.maxFee)})`);

    return parts.join(' ');
  };

  // Preview calculation
  const previewFee = calculateFee(previewAmount, formData);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Configuração de Taxas</h1>
          <p className="text-surface-400 mt-1">
            Configure as taxas cobradas por tipo de operação
          </p>
        </div>
        <button
          onClick={openNewRuleModal}
          className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-400 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nova Regra
        </button>
      </div>

      {/* Rules List */}
      <div className="space-y-3">
        {rules.length === 0 ? (
          <Card className="p-8 text-center">
            <Calculator className="w-12 h-12 text-surface-500 mx-auto mb-4" />
            <p className="text-surface-400 mb-2">Nenhuma regra de taxa configurada</p>
            <p className="text-xs text-surface-500">
              Clique em "Nova Regra" para adicionar uma configuração de taxa
            </p>
          </Card>
        ) : (
          rules.map((rule: FeeRule) => (
            <Card key={rule.id} className="p-4">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-surface-700 rounded-lg">
                  {rule.calculationType === 'PERCENTAGE' && <Percent className="w-5 h-5 text-primary-400" />}
                  {rule.calculationType === 'FIXED' && <DollarSign className="w-5 h-5 text-green-400" />}
                  {rule.calculationType === 'PERCENTAGE_PLUS_FIXED' && <Calculator className="w-5 h-5 text-yellow-400" />}
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-white">
                      {getOperationLabel(rule.operationType)}
                    </h3>
                    <Badge variant="primary">
                      {getCalculationLabel(rule.calculationType)}
                    </Badge>
                  </div>
                  <p className="text-sm text-primary-400 font-mono mt-1">
                    {formatRuleDescription(rule)}
                  </p>
                  {rule.description && (
                    <p className="text-xs text-surface-500 mt-1">{rule.description}</p>
                  )}
                </div>

                {/* Preview */}
                <div className="text-right">
                  <p className="text-xs text-surface-500">Para R$ 100,00:</p>
                  <p className="text-sm font-medium text-white">
                    {formatCurrency(calculateFee(10000, rule))}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEditModal(rule)}
                    className="p-2 text-surface-400 hover:text-white hover:bg-surface-700 rounded-lg transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => rule.id && handleDelete(rule.id)}
                    className="p-2 text-surface-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingRule ? 'Editar Regra de Taxa' : 'Nova Regra de Taxa'}
      >
        <div className="space-y-4">
          {/* Operation Type */}
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-surface-200">
              Tipo de Operação <span className="text-red-400">*</span>
            </label>
            <select
              value={formData.operationType}
              onChange={(e) => setFormData({ ...formData, operationType: e.target.value })}
              className="w-full px-4 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
            >
              <option value="">Selecione...</option>
              {operationTypes.map((op) => (
                <option key={op.value} value={op.value}>
                  {op.label}
                </option>
              ))}
            </select>
          </div>

          {/* Calculation Type */}
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-surface-200">
              Tipo de Cálculo
            </label>
            <div className="grid grid-cols-3 gap-2">
              {calculationTypes.map((type) => {
                const Icon = type.icon;
                const isSelected = formData.calculationType === type.value;

                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, calculationType: type.value as any })}
                    className={cn(
                      'flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all',
                      isSelected
                        ? 'border-primary-500 bg-primary-500/10'
                        : 'border-surface-600 bg-surface-800 hover:border-surface-500'
                    )}
                  >
                    <Icon className={cn(
                      'w-5 h-5',
                      isSelected ? 'text-primary-400' : 'text-surface-400'
                    )} />
                    <span className={cn(
                      'text-xs font-medium',
                      isSelected ? 'text-white' : 'text-surface-300'
                    )}>
                      {type.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Values */}
          <div className="grid grid-cols-2 gap-4">
            {/* Percentage */}
            {(formData.calculationType === 'PERCENTAGE' || formData.calculationType === 'PERCENTAGE_PLUS_FIXED') && (
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-surface-200">
                  Percentual (%)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    value={formData.percentageValue || ''}
                    onChange={(e) => setFormData({ ...formData, percentageValue: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-white focus:outline-none focus:border-primary-500 pr-8"
                  />
                  <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-500" />
                </div>
              </div>
            )}

            {/* Fixed Value */}
            {(formData.calculationType === 'FIXED' || formData.calculationType === 'PERCENTAGE_PLUS_FIXED') && (
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-surface-200">
                  Valor Fixo (centavos)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={formData.fixedValue || ''}
                    onChange={(e) => setFormData({ ...formData, fixedValue: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
                  />
                </div>
                <p className="text-xs text-surface-500">
                  {formData.fixedValue ? formatCurrency(formData.fixedValue) : 'R$ 0,00'}
                </p>
              </div>
            )}
          </div>

          {/* Min/Max */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-surface-200">
                Taxa Mínima (centavos)
              </label>
              <input
                type="number"
                value={formData.minFee || ''}
                onChange={(e) => setFormData({ ...formData, minFee: parseInt(e.target.value) || undefined })}
                placeholder="Sem mínimo"
                className="w-full px-4 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
              />
              {formData.minFee && (
                <p className="text-xs text-surface-500">{formatCurrency(formData.minFee)}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-surface-200">
                Taxa Máxima (centavos)
              </label>
              <input
                type="number"
                value={formData.maxFee || ''}
                onChange={(e) => setFormData({ ...formData, maxFee: parseInt(e.target.value) || undefined })}
                placeholder="Sem máximo"
                className="w-full px-4 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
              />
              {formData.maxFee && (
                <p className="text-xs text-surface-500">{formatCurrency(formData.maxFee)}</p>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-surface-200">
              Descrição (opcional)
            </label>
            <input
              type="text"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Ex: Taxa padrão para recebimentos"
              className="w-full px-4 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
            />
          </div>

          {/* Preview Calculator */}
          <div className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
            <div className="flex items-center gap-2 mb-3">
              <Calculator className="w-4 h-4 text-primary-400" />
              <span className="text-sm font-medium text-white">Preview do Cálculo</span>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <label className="text-xs text-surface-400 w-24">Valor teste:</label>
                <input
                  type="number"
                  value={previewAmount}
                  onChange={(e) => setPreviewAmount(parseInt(e.target.value) || 0)}
                  className="flex-1 px-3 py-1.5 bg-surface-800 border border-surface-600 rounded text-sm text-white focus:outline-none focus:border-primary-500"
                />
                <span className="text-xs text-surface-500 w-24">
                  {formatCurrency(previewAmount)}
                </span>
              </div>

              <div className="flex items-center justify-between p-3 bg-surface-900 rounded-lg">
                <span className="text-sm text-surface-300">Taxa calculada:</span>
                <span className="text-lg font-bold text-primary-400">
                  {formatCurrency(previewFee)}
                </span>
              </div>

              <p className="text-xs text-surface-500">
                Fórmula: max(min, min(max, fixo + valor × percentual / 100))
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-surface-700">
            <button
              onClick={closeModal}
              className="px-4 py-2 text-sm font-medium text-surface-300 hover:text-white transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={!formData.operationType || saveMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saveMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {editingRule ? 'Salvar Alterações' : 'Criar Regra'}
            </button>
          </div>

          {/* Error */}
          {saveMutation.isError && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-400" />
              <p className="text-sm text-red-400">
                {(saveMutation.error as any)?.message || 'Erro ao salvar regra'}
              </p>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default FeeConfig;
